import {
  Button,
  Caption1,
  Field,
  Input,
  Subtitle1,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { createProcurerUser, getAccountRequestToken } from "../services/usersService";
import { Department, DepartmentGroup } from "../models/organizations";
import { getDepartmentGroupsByDomain } from "../services/departmentService";
import { createAccountRequest } from "../services/taskService";
import { getModulesByDomain } from "../services/moduleService";
import { ProcurerUserCreateDTO } from "../models/users";
import ConfirmationDialog from "./dialogs/ConfirmationDialog";

type AccountRequestFormValues = {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  moduleID?: string;
  title?: string;
  phoneNumber?: string;
};

const AccountRequest = () => {
  const { register, handleSubmit, watch, control, formState: { errors, isSubmitting } } = useForm<AccountRequestFormValues>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modules, setModules] = useState<any[]>([]);
  const [token, setToken] = useState<any>();
  const [isProcurerAccount, setIsProcurerAccount] = useState<boolean>(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);

  const tokenString = searchParams.get("token");

  const password = watch("password");

  useEffect(() => {
    const fetchTokenData = async () => {
      if (tokenString) {
        try {
          setErrorMessage(null);
          const tokenData = await getAccountRequestToken(tokenString);
          setToken(tokenData);
          setIsProcurerAccount(tokenData.tokenData.IsProcurerAccount);
          console.log("Token data:", tokenData);
          console.log(tokenData.tokenData.IsProcurerAccount);
        } catch (err: any) {
          setTokenError(true);
        }
      }
    };

    fetchTokenData();
  }, [tokenString]);

  useEffect(() => {
    const getDepartments = async (email: string) => {
      const modulesResult = await getModulesByDomain(email);
      console.log(modulesResult);
      setModules(modulesResult.filter((m: any) => 
        m.moduleName && 
        !m.moduleName.toLowerCase().includes("root")
      ));
    }

    if(token && token.tokenData) {
      getDepartments(token.tokenData.Email.split('@').pop().trim());
    }

  }, [token]);

  const onSubmit = async (data: AccountRequestFormValues) => {
    console.log("Submitting account request:", data);
    try {
      if(!token.tokenData.IsProcurerAccount){
        setErrorMessage(null);
        setSuccessMessage(null);
        
        console.log("moduleID before conversion:", data.moduleID, typeof data.moduleID);
        
        const account: any = {
        taskDepartmentID: data.moduleID ? +data.moduleID : undefined,
        taskName: 'Ansökan om konto',
        taskDescription: `${token.tokenData.Email} ansöker om konto. Du kan välja behörighetsgrupper genom att kryssa i eller av.`, 
        taskModuleID: data.moduleID ? +data.moduleID : undefined,
        taskData: {
            Email: token.tokenData.Email,
            LastName: data.lastName,
            FirstName: data.firstName,
            Password: data.password,
            PhoneNumber: data.phoneNumber,
            Title: data.title,
            TokenHash: tokenString
          }
        }

        console.log("Account object:", account);
        await createAccountRequest(account)

        setSuccessMessage("Din ansökan har skickats! Vi kontaktar dig inom kort.");
        console.log("Account request:", data);
        setShowSuccessDialog(true);
      }
      else{
        const account: ProcurerUserCreateDTO = {
          Email: token.tokenData.Email,
          FirstName: data.firstName,
          LastName: data.lastName,
          Password: data.password,
          Title: data.title,
          PhoneNumber: data.phoneNumber,
          UserGroupID: token.tokenData.UserGroupID,
          IsUserGroupAdmin: token.tokenData.IsUserGroupAdmin,
          TokenHash: tokenString ?? undefined,
        }

        await createProcurerUser(account);
        setShowSuccessDialog(true);
      }
    } catch (err: any) {
      setErrorMessage("Kunde inte skicka ansökan. Försök igen senare.");
    }
  };

  return (
    <div className="bg-[#ebebeb]">
      <div className="flex justify-center h-[10vh] bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b w-full text-white"/>
      <div className="-mt-10">
        <div className="bg-white gap-5 flex flex-col rounded shadow-md w-[500px] m-auto overflow-y-auto p-5">
          <div className="flex flex-col">
            <Subtitle1>Ansök om konto</Subtitle1>
            {!tokenError && <Caption1>
              Fyll i formuläret nedan så kontaktar vi dig för att skapa ditt konto
            </Caption1>}
          </div>

          {tokenError && (
            <Caption1 className="text-red-500">Ogiltig eller utgången ansökan. Var god ansök på nytt.</Caption1>
          )}

          {!tokenError && <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Field label="Förnamn" validationMessage={errors.firstName?.message} required>
              <Input 
                {...register("firstName", { 
                  required: "Förnamn krävs"
                })} 
                placeholder="Ange ditt förnamn"
              />
            </Field>

            <Field label="Efternamn" validationMessage={errors.lastName?.message} required>
              <Input 
                {...register("lastName", { 
                  required: "Efternamn krävs"
                })} 
                placeholder="Ange ditt efternamn"
              />
            </Field>

            <Field label="Lösenord" validationMessage={errors.password?.message} required>
              <Input 
                type="password"
                {...register("password", { 
                  required: "Lösenord krävs",
                  minLength: {
                    value: 8,
                    message: "Lösenordet måste vara minst 8 tecken"
                  }
                })} 
                placeholder="Minst 8 tecken"
              />
            </Field>

            <Field label="Bekräfta lösenord" validationMessage={errors.confirmPassword?.message} required>
              <Input 
                type="password"
                {...register("confirmPassword", { 
                  required: "Bekräfta lösenord krävs",
                  validate: value => value === password || "Lösenorden matchar inte"
                })} 
                placeholder="Ange lösenordet igen"
              />
            </Field>


            <Field label="Titel" validationMessage={errors.title?.message}>
              <Input 
                {...register("title")} 
                placeholder="T.ex. Upphandlare (valfritt)"
              />
            </Field>

            <Field label="Telefonnummer" validationMessage={errors.phoneNumber?.message}>
              <Input 
                {...register("phoneNumber", {
                  pattern: {
                    value: /^[0-9\s\-\+\(\)]+$/,
                    message: "Ogiltigt telefonnummer"
                  }
                })} 
                placeholder="T.ex. 070-123 45 67 (valfritt)"
              />
            </Field>

            {successMessage && (
              <Caption1 className="text-green-600">{successMessage}</Caption1>
            )}

            {errorMessage && (
              <Caption1 className="text-red-500">{errorMessage}</Caption1>
            )}

            <Button appearance="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Skickar ansökan..." : "Skicka ansökan"}
            </Button>
          </form>}
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showSuccessDialog}
        title="Konto skapat"
        text="Ditt konto har skapats. Du kommer nu att omdirigeras till inloggningssidan."
        onConfirm={() => navigate("/login")}
        onClose={() => navigate("/login")}
      />
    </div>
  );
};

export default AccountRequest;
